import { DecimalPipe } from '@angular/common';
import { Component, computed, effect, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { ButtonComponent, IconComponent } from '../base';

@Component({
  selector: 'spx-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['paginator.component.scss'],
  imports: [MatSelect, MatOption, MatFormField, ReactiveFormsModule, ButtonComponent, IconComponent, DecimalPipe],
})
export class PaginatorComponent implements OnInit, OnDestroy {
  public paginationOptions = input<number[] | undefined>();
  public paginationLabel = input<string>('');
  public paginationSizeLabel = input<string>('');
  public pageSize = input<number>(12);
  public pageNumber = input.required<number>();
  public totalElements = input.required<number>();
  public internalPageSize = signal(12);
  public lastPageIndex = computed(() => {
    const total = this.totalElements();
    const size = this.internalPageSize();
    return total > 0 ? Math.ceil(total / size) - 1 : 0;
  });
  public internalPageIndex = signal(0);
  public pageNumbers = computed(() =>
    [this.internalPageIndex() - 1, this.internalPageIndex(), this.internalPageIndex() + 1].filter(
      (n) => n >= 0 && n <= this.lastPageIndex(),
    ),
  );
  public isFirstPage = computed(() => this.internalPageIndex() === 0);
  public isLastPage = computed(() => this.lastPageIndex() === 0 || this.internalPageIndex() === this.lastPageIndex());
  public firstDisplayedItem = computed(() => this.internalPageIndex() * this.internalPageSize() + 1);
  public lastDisplayedItem = computed(() => Math.min((this.internalPageIndex() + 1) * this.internalPageSize(), this.totalElements()));
  public pageChange = output<PageEvent>();
  public pageSizeFormControl: FormControl;
  protected select = viewChild(MatSelect);
  private pageSizeControlSubscription: Subscription;
  private pageChangeDebounceSubscription: Subscription;

  private delayer = new Subject<PageEvent>();

  constructor() {
    effect(() => {
      this.internalPageSize.set(this.pageSize());
      this.internalPageIndex.set(this.pageNumber());
    });
  }

  ngOnInit() {
    this.pageSizeFormControl = new FormControl(this.pageSize());
    this.pageSizeControlSubscription = this.pageSizeFormControl.valueChanges.subscribe((value) => this.changePageSize(value));
    this.pageChangeDebounceSubscription = this.delayer.pipe(debounceTime(400)).subscribe((value) => {
      this.pageChange.emit(value);
    });
  }

  ngOnDestroy() {
    this.pageSizeControlSubscription?.unsubscribe();
    this.pageChangeDebounceSubscription?.unsubscribe();
  }

  public changePageIndex(pageIndex: number): void {
    this.internalPageIndex.set(pageIndex);
    this.delayer.next({ pageSize: this.internalPageSize(), pageIndex } as PageEvent);
  }

  public changePageSize(pageSize: number): void {
    if (pageSize !== this.internalPageSize()) {
      this.internalPageSize.set(pageSize);
      this.delayer.next({ pageSize, pageIndex: 0 } as PageEvent);
    }
  }

  public openSelect(): void {
    const select = this.select();
    if (select) {
      const trigger = select.trigger.nativeElement;
      const rect = trigger.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < 200) {
        select.panelClass = 'select-panel-open-upward';
      }
    }
  }
}
